import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, Skeleton, StatusBadge, Switch, type Tone } from '../components/ui.tsx';
import { locale, t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { dateTime, day, money, parseMoney, todayIso } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { Bars, downloadCsv, Kpi, Table } from './Reports.tsx';

/**
 * The register of fixed assets: what the business owns, where it is and who holds it, what it
 * cost and what it is worth after depreciation. Assets are handed over with a printed record,
 * counted once a year and taken out of use with a decision.
 */

type State = 'in_use' | 'in_store' | 'repair' | 'disposed';
type Method = 'straight_line' | 'none';
type Source = 'bought' | 'existing' | 'donated' | 'built';
type Disposal = 'sold' | 'scrapped' | 'lost' | 'donated';
type Tab = 'register' | 'depreciation' | 'counts' | 'categories';
type View = 'all' | 'in_use' | 'in_store' | 'repair' | 'disposed';
interface Asset {
  id: string;
  tag: string;
  name: string;
  category: string;
  category_name: string;
  serial: string | null;
  description: string | null;
  source: Source;
  acquired_on: string;
  cost: number;
  residual: number;
  life_months: number;
  method: Method;
  start: string;
  depreciated_before: number;
  months_before: number;
  location: string | null;
  holder: string | null;
  state: State;
  supplier: string | null;
  bill: string | null;
  disposed_on: string | null;
  disposal: Disposal | null;
  disposal_amount: number;
  disposal_reason: string | null;
  disposal_ref: string | null;
  depreciated: number;
  net: number;
  monthly: number;
  done: boolean;
}
interface Category {
  id: string;
  code: string | null;
  name: string;
  life_months: number;
  method: Method;
  active: boolean;
  n: number;
  cost: number;
}
interface Overview {
  today: string;
  totals: { assets: number; cost: number; net: number; depreciated: number; year_depreciation: number; in_use: number; in_store: number; repair: number; disposed: number };
  categories: Category[];
  locations: { location: string; n: number }[];
  depreciation: { due: string; behind: number; last: { number: number; until: string; total: number } | null };
  open_count: { id: string; name: string } | null;
  next_tag: string;
  can: { manage: boolean; dispose: boolean; count: boolean };
  accounting: boolean;
}
interface History {
  id: string;
  kind: 'added' | 'moved' | 'state' | 'edited' | 'disposed' | 'counted';
  date: string;
  location: string | null;
  holder: string | null;
  note: string | null;
  data: Record<string, any> | null;
  created_at: string;
  by_name: string | null;
}
interface Detail {
  asset: Asset;
  history: History[];
  years: { year: string; amount: number; months: number }[];
  plan: { months: number; done: number; next: { period: string; amount: number } | null; ends: string | null };
  can: { manage: boolean; dispose: boolean };
}
interface Options {
  locations: string[];
  holders: string[];
  categories: Omit<Category, 'n' | 'cost'>[];
  bills: { id: string; number: number; date: string; total: number; tax: number; supplier: string }[];
  people: { id: string; name: string }[];
}

const TABS: { key: Tab; icon: IconName }[] = [
  { key: 'register', icon: 'archive' },
  { key: 'depreciation', icon: 'trendDown' },
  { key: 'counts', icon: 'list' },
  { key: 'categories', icon: 'layers' },
];
const VIEWS: View[] = ['all', 'in_use', 'in_store', 'repair', 'disposed'];
const STATE: Record<State, { tone: Tone; icon: IconName }> = {
  in_use: { tone: 'ok', icon: 'check' },
  in_store: { tone: 'neutral', icon: 'box' },
  repair: { tone: 'warn', icon: 'settings' },
  disposed: { tone: 'neutral', icon: 'x' },
};
const SOURCES: Source[] = ['bought', 'existing', 'donated', 'built'];
const DISPOSALS: Disposal[] = ['sold', 'scrapped', 'lost', 'donated'];

const stateLabel = (s: State) => t(`ast.st_${s}` as MessageKey);
const StateBadge = ({ s }: { s: State }) => <StatusBadge tone={STATE[s].tone} icon={STATE[s].icon} label={stateLabel(s)} />;
const life = (months: number) => (months % 12 === 0 ? tn('ast.n_years', months / 12) : tn('ast.n_months', months));
const monthName = (m: string) => {
  const [y, mm] = m.split('-').map(Number) as [number, number];
  return new Intl.DateTimeFormat(locale(), { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(y, mm - 1, 1)));
};
const addMonths = (m: string, n: number) => {
  const [y, mm] = m.split('-').map(Number) as [number, number];
  const i = y * 12 + (mm - 1) + n;
  return `${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, '0')}`;
};
const lek = (minor: number) => (minor ? (minor / 100).toFixed(2).replace(/\.00$/, '') : '');

export function Assets() {
  const [params, setParams] = useSearchParams();
  const tab = (TABS.some((x) => x.key === params.get('tab')) ? params.get('tab') : 'register') as Tab;
  const { data: o, reload } = useAction<Overview>('assets.overview');
  const [adding, setAdding] = useState(false);
  const [depreciating, setDepreciating] = useState(false);
  const [version, setVersion] = useState(0);
  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) (v === null ? p.delete(k) : p.set(k, v));
    setParams(p, { replace: true });
  };
  const changed = async () => {
    setVersion((v) => v + 1);
    await reload();
  };
  const d = o?.depreciation;

  return (
    <Shell>
      <div className="page reports assets">
        <div className="page-head">
          <div>
            <h1>{t('ast.title')}</h1>
            <p className="muted">{t('ast.subtitle')}</p>
          </div>
          {o?.can.manage ? (
            <div className="page-actions">
              <Button icon="trendDown" onClick={() => setDepreciating(true)}>
                {t('ast.depreciate')}
              </Button>
              <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>
                {t('ast.add')}
              </Button>
            </div>
          ) : null}
        </div>
        {!o ? (
          <Skeleton h={110} r="var(--radius-lg)" />
        ) : (
          <section className="rep-kpis" aria-label={t('ast.at_a_glance')}>
            <Kpi label={t('ast.k_assets')} value={String(o.totals.assets)} hint={[tn('ast.k_in_use', o.totals.in_use), o.totals.repair ? tn('ast.k_repair', o.totals.repair) : null].filter(Boolean).join(' · ')} warn={o.totals.repair > 0} />
            <Kpi label={t('ast.k_cost')} value={money(o.totals.cost)} hint={tn('ast.k_categories', o.categories.filter((c) => c.n).length)} />
            <Kpi label={t('ast.k_net')} value={money(o.totals.net)} hint={t('ast.k_depreciated', { amount: money(o.totals.depreciated) })} />
            <Kpi
              label={t('ast.k_year', { year: o.today.slice(0, 4) })}
              value={money(o.totals.year_depreciation)}
              hint={d!.behind ? tn('ast.k_behind', d!.behind, { month: monthName(d!.due) }) : d!.last ? t('ast.k_up_to', { month: monthName(d!.last.until) }) : t('ast.k_none_yet')}
              warn={d!.behind > 0}
            />
          </section>
        )}

        <div className="segmented tabs ast-tabs" role="tablist" aria-label={t('ast.sections')}>
          {TABS.map((x) => (
            <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => set({ tab: x.key === 'register' ? null : x.key, view: null, q: null })}>
              <Icon name={x.icon} size={16} />
              {t(`ast.tab_${x.key}` as MessageKey)}
              {x.key === 'depreciation' && d?.behind ? <span className="count">{d.behind}</span> : null}
              {x.key === 'counts' && o?.open_count ? <span className="count">1</span> : null}
            </button>
          ))}
        </div>

        {!o ? null : tab === 'register' ? (
          <Register key={version} o={o} params={params} set={set} onChange={changed} onAdd={() => setAdding(true)} />
        ) : tab === 'depreciation' ? (
          <DepreciationTab key={version} o={o} onRun={() => setDepreciating(true)} />
        ) : tab === 'counts' ? (
          <CountsTab key={version} o={o} onChange={changed} />
        ) : (
          <CategoriesTab o={o} onChange={changed} />
        )}
      </div>
      {adding && o ? (
        <AssetForm
          o={o}
          onClose={() => setAdding(false)}
          onDone={async () => {
            setAdding(false);
            await changed();
          }}
        />
      ) : null}
      {depreciating && o ? (
        <RunDialog
          o={o}
          onClose={() => setDepreciating(false)}
          onDone={async () => {
            setDepreciating(false);
            await changed();
          }}
        />
      ) : null}
    </Shell>
  );
}

/* ---------------------------------------------------------------- Register */

function Register({ o, params, set, onChange, onAdd }: { o: Overview; params: URLSearchParams; set: (n: Record<string, string | null>) => void; onChange: () => Promise<void>; onAdd: () => void }) {
  const view = (VIEWS.includes(params.get('view') as View) ? params.get('view') : 'all') as View;
  const location = params.get('where');
  const category = params.get('cat');
  const [q, setQ] = useState(params.get('q') ?? '');
  const { data: rows, reload } = useAction<Asset[]>('assets.list', { view, location, category, q: q.trim() || null });
  const [open, setOpen] = useState<string | null>(params.get('asset'));
  const [picked, setPicked] = useState<string[]>([]);
  const [moving, setMoving] = useState<string[] | null>(null);
  const [handover, setHandover] = useState<number | null>(null);
  const show = (id: string | null) => {
    setOpen(id);
    set({ asset: id });
  };
  const changed = async () => {
    await Promise.all([reload(), onChange()]);
  };
  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const count = (v: View) => (v === 'all' ? o.totals.assets : o.totals[v]);
  const csv = () =>
    downloadCsv(
      `${t('ast.file')}.csv`,
      [t('ast.c_tag'), t('ast.name'), t('ast.category'), t('ast.serial'), t('ast.acquired_on'), t('ast.where'), t('ast.holder'), t('ast.cost'), t('ast.depreciated'), t('ast.net'), t('ast.c_state')],
      (rows ?? []).map((a) => [a.tag, a.name, a.category_name, a.serial ?? '', a.acquired_on, a.location ?? '', a.holder ?? '', a.cost / 100, a.depreciated / 100, a.net / 100, stateLabel(a.state)]),
    );
  const totals = (rows ?? []).reduce((s, a) => ({ cost: s.cost + a.cost, net: s.net + a.net }), { cost: 0, net: 0 });

  return (
    <>
      <div className="toolbar ast-toolbar">
        <div className="segmented tabs" role="tablist" aria-label={t('ast.show')}>
          {VIEWS.map((v) => (
            <button key={v} role="tab" aria-selected={view === v} className={view === v ? 'active' : ''} onClick={() => (setPicked([]), set({ view: v === 'all' ? null : v }))}>
              {t(`ast.v_${v}` as MessageKey)}
              {count(v) ? <span className="count">{count(v)}</span> : null}
            </button>
          ))}
        </div>
        <span className="row-actions ast-filters">
          <label className="search">
            <Icon name="search" size={16} />
            <input className="input" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('ast.search')} aria-label={t('ast.search')} />
          </label>
          {o.locations.length > 1 ? (
            <select className="input" value={location ?? ''} onChange={(e) => set({ where: e.target.value || null })} aria-label={t('ast.where')}>
              <option value="">{t('ast.all_places')}</option>
              {o.locations.map((l) => (
                <option key={l.location} value={l.location}>
                  {l.location} ({l.n})
                </option>
              ))}
            </select>
          ) : null}
          {o.categories.length > 1 ? (
            <select className="input" value={category ?? ''} onChange={(e) => set({ cat: e.target.value || null })} aria-label={t('ast.category')}>
              <option value="">{t('ast.all_categories')}</option>
              {o.categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          ) : null}
          <Button icon="download" onClick={csv} disabled={!rows?.length}>
            {t('ast.spreadsheet')}
          </Button>
        </span>
      </div>
      {picked.length ? (
        <div className="ast-picked">
          <span className="strong">{tn('ast.n_picked', picked.length)}</span>
          <Button size="sm" variant="ghost" onClick={() => setPicked([])}>
            {t('ast.clear')}
          </Button>
          <Button size="sm" variant="primary" icon="transfer" onClick={() => setMoving(picked)}>
            {t('ast.hand_over')}
          </Button>
        </div>
      ) : null}

      {!rows ? (
        <Skeleton h={300} r="var(--radius-lg)" />
      ) : !rows.length ? (
        q.trim() || location || category ? (
          <EmptyState icon="search" title={t('ast.none_found')} body={t('ast.none_found_body')} />
        ) : view === 'all' ? (
          <EmptyState
            icon="archive"
            title={t('ast.empty')}
            body={t('ast.empty_body')}
            action={
              o.can.manage ? (
                <Button variant="primary" icon="plus" onClick={onAdd}>
                  {t('ast.add')}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <EmptyState icon="checkCircle" title={t(`ast.empty_${view}` as MessageKey)} />
        )
      ) : (
        <Table
          view="assets.register"
          head={[t('ast.c_tag'), t('ast.c_asset'), t('ast.c_where'), t('ast.cost'), t('ast.net'), t('ast.c_state')]}
          phoneHide={[0, 2, 3, 5]}
          left={[0, 1, 2]}
          rows={rows.map((a) => ({
            key: a.id,
            onClick: () => show(a.id),
            sort: [a.tag, a.name.toLowerCase(), a.location?.toLowerCase() ?? null, a.cost, a.net, a.state],
            cells: [
              <span className="ast-tag">
                {o.can.manage && a.state !== 'disposed' ? (
                  <input
                    type="checkbox"
                    checked={picked.includes(a.id)}
                    onChange={() => toggle(a.id)}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={t('ast.pick_x', { tag: a.tag })}
                  />
                ) : null}
                <span className="tabular strong">{a.tag}</span>
              </span>,
              <span className="vat-party">
                <span>
                  <span className="ast-phone-tag tabular strong">{a.tag} · </span>
                  {a.name}
                </span>
                <span className="muted">{[a.category_name, a.serial].filter(Boolean).join(' · ')}</span>
              </span>,
              <span className="vat-party">
                <span>{a.location ?? '—'}</span>
                <span className="muted">{a.holder ?? ' '}</span>
              </span>,
              <span className="tabular">{money(a.cost)}</span>,
              <span className="tabular">{money(a.net)}</span>,
              <StateBadge s={a.state} />,
            ],
          }))}
          foot={rows.length > 1 ? [tn('ast.n_assets', rows.length), '', '', money(totals.cost), money(totals.net), ''] : null}
        />
      )}
      {open ? (
        <AssetDialog
          id={open}
          o={o}
          onClose={() => show(null)}
          onChange={changed}
          onMove={(id) => setMoving([id])}
          onHandover={setHandover}
        />
      ) : null}
      {moving ? (
        <MoveDialog
          ids={moving}
          rows={rows ?? []}
          onClose={() => setMoving(null)}
          onDone={async (n) => {
            setMoving(null);
            setPicked([]);
            await changed();
            setHandover(n);
          }}
        />
      ) : null}
      {handover ? <HandoverPrint number={handover} onDone={() => setHandover(null)} /> : null}
    </>
  );
}

/* ---------------------------------------------------------------- One asset */

function AssetDialog({ id, o, onClose, onChange, onMove, onHandover }: { id: string; o: Overview; onClose: () => void; onChange: () => Promise<void>; onMove: (id: string) => void; onHandover: (n: number) => void }) {
  const { data, reload } = useAction<Detail>('assets.get', { id });
  const [ask, setAsk] = useState<null | 'edit' | 'dispose'>(null);
  const done = async () => {
    setAsk(null);
    await Promise.all([reload(), onChange()]);
  };
  if (!data) {
    return (
      <Dialog title={t('ast.asset')} onClose={onClose} size="lg">
        <Skeleton h={260} />
      </Dialog>
    );
  }
  const a = data.asset;
  const fact = (label: string, value: React.ReactNode) =>
    value ? (
      <>
        <dt>{label}</dt>
        <dd>{value}</dd>
      </>
    ) : null;
  const usedPct = a.cost ? Math.round((a.depreciated / (a.cost - a.residual || a.cost)) * 100) : 0;

  return (
    <Dialog
      title={`${a.tag} · ${a.name}`}
      onClose={onClose}
      size="lg"
      footer={
        data.can.manage || data.can.dispose ? (
          <>
            <span className="foot-actions">
              {data.can.manage ? (
                <Button variant="ghost" icon="edit" onClick={() => setAsk('edit')}>
                  {t('ast.edit')}
                </Button>
              ) : null}
              {data.can.dispose ? (
                <Button variant="ghost" icon="archive" onClick={() => setAsk('dispose')}>
                  {t('ast.dispose')}
                </Button>
              ) : null}
            </span>
            {data.can.manage ? (
              <Button variant="primary" icon="transfer" onClick={() => onMove(a.id)}>
                {t('ast.move')}
              </Button>
            ) : null}
          </>
        ) : undefined
      }
    >
      <div className="form-stack">
        <div className="item-summary">
          <div>
            <span className="muted">{t('ast.c_tag')}</span>
            <strong className="tabular">{a.tag}</strong>
          </div>
          <div>
            <span className="muted">{t('ast.category')}</span>
            <strong>{a.category_name}</strong>
          </div>
          <div>
            <span className="muted">{t('ast.where')}</span>
            <strong>{a.location ?? '—'}</strong>
          </div>
          <StateBadge s={a.state} />
        </div>
        {a.state === 'disposed' ? (
          <p className="callout callout-warn">
            <Icon name="archive" size={16} />
            <span>
              {t(`ast.disposed_${a.disposal}` as MessageKey, { day: day(a.disposed_on!), amount: money(a.disposal_amount) })} {a.disposal_reason}
              {a.disposal_ref ? ` (${a.disposal_ref})` : ''}
            </span>
          </p>
        ) : null}

        <section className="ast-value" aria-label={t('ast.value')}>
          <div>
            <span className="muted">{t('ast.cost')}</span>
            <strong className="tabular">{money(a.cost)}</strong>
          </div>
          <div>
            <span className="muted">{t('ast.depreciated')}</span>
            <strong className="tabular">{money(a.depreciated)}</strong>
          </div>
          <div>
            <span className="muted">{t('ast.net')}</span>
            <strong className="tabular">{money(a.net)}</strong>
          </div>
          {a.method === 'none' ? (
            <p className="muted small ast-value-note">{t('ast.not_depreciated')}</p>
          ) : (
            <div className="ast-value-bar">
              <span className="ast-bar" aria-hidden="true">
                <span style={{ width: `${Math.min(100, usedPct)}%` }} />
              </span>
              <span className="muted small">
                {t('ast.plan_line', { done: data.plan.done, months: data.plan.months })}
                {data.plan.next && a.state !== 'disposed' ? ` · ${t('ast.next_month', { amount: money(data.plan.next.amount), month: monthName(data.plan.next.period) })}` : ''}
                {data.plan.ends ? ` · ${t('ast.ends', { month: monthName(data.plan.ends) })}` : ''}
              </span>
            </div>
          )}
        </section>

        <dl className="appr-facts">
          {fact(t('ast.serial'), a.serial)}
          {fact(t('ast.holder'), a.holder)}
          {fact(t('ast.acquired_on'), `${day(a.acquired_on)} · ${t(`ast.src_${a.source}` as MessageKey)}`)}
          {fact(t('ast.supplier'), a.supplier)}
          {fact(t('ast.life'), a.method === 'none' ? null : `${life(a.life_months)}${a.residual ? ` · ${t('ast.residual_x', { amount: money(a.residual) })}` : ''}`)}
          {fact(t('ast.before'), a.months_before ? t('ast.before_x', { amount: money(a.depreciated_before), months: a.months_before }) : null)}
          {fact(t('ast.description'), a.description)}
        </dl>

        {data.years.length ? (
          <section className="item-moves">
            <h3>{t('ast.by_year')}</h3>
            <Table
              head={[t('ast.year'), t('ast.months'), t('ast.depreciation')]}
              left={[0]}
              rows={data.years.map((y) => ({ key: y.year, cells: [y.year, String(y.months), <span className="tabular">{money(y.amount)}</span>] }))}
            />
          </section>
        ) : null}

        <section className="item-moves">
          <h3>{t('ast.history')}</h3>
          <ol className="prot-history">
            {data.history.map((h) => (
              <li key={h.id} className={`prot-h ast-h-${h.kind}`}>
                <span className="prot-h-dot" aria-hidden="true" />
                <span className="prot-h-body">
                  <span className="strong">{historyTitle(h)}</span>
                  {h.kind === 'edited' && h.data?.changes ? (
                    <span className="muted small">
                      {Object.entries(h.data.changes as Record<string, { from: unknown; to: unknown }>)
                        .map(([k, v]) => t('ast.changed_field', { field: t(`ast.f_${k}` as MessageKey), from: shown(k, v.from), to: shown(k, v.to) }))
                        .join(' · ')}
                    </span>
                  ) : null}
                  {h.note ? <span className="prot-h-text">{h.note}</span> : null}
                  <span className="muted small">
                    {[day(h.date), h.by_name, h.kind !== 'added' ? dateTime(h.created_at) : null].filter(Boolean).join(' · ')}
                    {h.data?.handover ? (
                      <>
                        {' · '}
                        <button className="link-button" onClick={() => onHandover(h.data!.handover)}>
                          {t('ast.handover_no', { n: h.data.handover })}
                        </button>
                      </>
                    ) : null}
                  </span>
                </span>
              </li>
            ))}
          </ol>
        </section>
      </div>
      {ask === 'edit' ? <AssetForm o={o} asset={a} onClose={() => setAsk(null)} onDone={done} /> : null}
      {ask === 'dispose' ? <DisposeDialog asset={a} onClose={() => setAsk(null)} onDone={done} /> : null}
    </Dialog>
  );
}

const shown = (k: string, v: unknown) => (v === null || v === undefined || v === '' ? '—' : k === 'residual' ? money(Number(v)) : k === 'life_months' ? life(Number(v)) : String(v));

function historyTitle(h: History) {
  const place = [h.location, h.holder].filter(Boolean).join(' · ') || '—';
  switch (h.kind) {
    case 'moved':
      return t('ast.h_moved', { to: place });
    case 'state':
      return t('ast.h_state', { state: stateLabel(h.data?.to?.state ?? 'in_use') });
    case 'counted':
      return h.data?.found ? t('ast.h_found', { name: h.data?.name ?? '' }) : t('ast.h_missing', { name: h.data?.name ?? '' });
    case 'disposed':
      return t(`ast.h_disposed_${h.data?.kind ?? 'scrapped'}` as MessageKey);
    case 'added':
      return t('ast.h_added', { place });
    default:
      return t('ast.h_edited');
  }
}

/* ---------------------------------------------------------------- Adding and editing */

function AssetForm({ o, asset, onClose, onDone }: { o: Overview; asset?: Asset; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const { toast } = useUi();
  const { data: opts } = useAction<Options>('assets.options');
  const editing = !!asset;
  const cats = (opts?.categories ?? []).filter((c) => c.active || c.id === asset?.category);
  const [name, setName] = useState(asset?.name ?? '');
  const [category, setCategory] = useState(asset?.category ?? '');
  const [quantity, setQuantity] = useState('1');
  const [tag, setTag] = useState('');
  const [serial, setSerial] = useState(asset?.serial ?? '');
  const [source, setSource] = useState<Source>(asset?.source ?? 'bought');
  const [acquired, setAcquired] = useState(asset?.acquired_on ?? todayIso());
  const [cost, setCost] = useState(asset ? lek(asset.cost) : '');
  const [bill, setBill] = useState('');
  const [supplier, setSupplier] = useState(asset?.supplier ?? '');
  const [lifeMonths, setLifeMonths] = useState(asset ? String(asset.life_months) : '');
  const [residual, setResidual] = useState(asset ? lek(asset.residual) : '');
  const [before, setBefore] = useState('');
  const [monthsBefore, setMonthsBefore] = useState('');
  const [location, setLocation] = useState('');
  const [holder, setHolder] = useState('');
  const [description, setDescription] = useState(asset?.description ?? '');
  const [tried, setTried] = useState(false);
  const cat = cats.find((c) => c.id === category);
  const n = Number(quantity) || 0;
  const minor = parseMoney(cost);
  const rest = residual.trim() ? parseMoney(residual) : 0;
  const prior = before.trim() ? parseMoney(before) : 0;
  const lifeN = lifeMonths.trim() ? Number(lifeMonths) : cat?.life_months ?? 0;
  const existing = source === 'existing';
  const errors = {
    name: name.trim() ? null : t('form.required'),
    category: category ? null : t('form.required'),
    cost: minor === null || minor <= 0 ? t('ast.cost_wrong') : null,
    quantity: Number.isInteger(n) && n >= 1 && n <= 500 ? null : t('ast.quantity_wrong'),
    life: Number.isInteger(lifeN) && lifeN >= 1 && lifeN <= 1200 ? null : t('ast.life_wrong'),
    residual: rest === null || (minor !== null && rest >= minor) ? t('ast.residual_wrong') : null,
    before: prior === null || (minor !== null && rest !== null && prior > minor - rest) ? t('ast.before_wrong') : null,
  };
  const pickedBill = opts?.bills.find((b) => b.id === bill);
  const save = async () => {
    setTried(true);
    if (Object.entries(errors).some(([k, e]) => e && (!editing || ['name', 'category', 'life', 'residual'].includes(k)))) return;
    const ok = await run(async () => {
      if (asset) {
        await action('assets.edit', {
          id: asset.id,
          name: name.trim(),
          category,
          serial: serial.trim() || null,
          description: description.trim() || null,
          supplier: supplier.trim() || null,
          life_months: lifeN,
          residual: rest,
        });
      } else {
        const r = await action<{ tag?: string; tags?: string[] }>('assets.add', {
          name: name.trim(),
          category,
          quantity: n,
          tag: n === 1 && tag.trim() ? tag.trim() : undefined,
          serial: n === 1 ? serial.trim() || null : null,
          source,
          acquired_on: acquired,
          cost: minor,
          residual: rest,
          life_months: lifeMonths.trim() ? lifeN : null,
          depreciated_before: existing ? prior : 0,
          months_before: existing ? Number(monthsBefore) || 0 : 0,
          bill: source === 'bought' && bill ? bill : null,
          supplier: supplier.trim() || null,
          location: location.trim() || null,
          holder: holder.trim() || null,
          description: description.trim() || null,
        });
        toast(r.tags ? t('ast.added_many', { from: r.tags[0]!, to: r.tags.at(-1)! }) : t('ast.added_one', { tag: r.tag ?? '' }));
      }
    }, editing ? t('ast.saved') : undefined);
    if (ok) await onDone();
  };
  const locked = editing && asset!.depreciated > asset!.depreciated_before;

  return (
    <Dialog
      title={editing ? t('ast.edit_x', { tag: asset!.tag }) : t('ast.add_title')}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon={editing ? 'check' : 'plus'} onClick={save} busy={busy}>
            {editing ? t('ast.save') : n > 1 ? t('ast.add_n', { n }) : t('ast.add')}
          </Button>
        </>
      }
    >
      {!opts ? (
        <Skeleton h={300} />
      ) : !cats.length ? (
        <EmptyState icon="layers" title={t('ast.no_categories')} body={t('ast.no_categories_body')} />
      ) : (
        <div className="form-stack">
          <Field label={t('ast.name')} hint={t('ast.name_hint')} error={tried ? errors.name : null} required>
            <input className="input" maxLength={160} value={name} onChange={(e) => setName(e.target.value)} data-autofocus />
          </Field>
          <div className="form-grid">
            <Field label={t('ast.category')} error={tried ? errors.category : null} required>
              <select className="input" value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="" disabled>
                  {t('ast.pick_category')}
                </option>
                {cats.map((c) => (
                  <option key={c.id} value={c.id}>
                    {[c.code, c.name].filter(Boolean).join(' ')}
                  </option>
                ))}
              </select>
            </Field>
            {editing ? null : (
              <Field label={t('ast.quantity')} hint={t('ast.quantity_hint')} error={tried ? errors.quantity : null}>
                <input className="input tabular" inputMode="numeric" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
              </Field>
            )}
            {editing || n > 1 ? null : (
              <Field label={t('ast.c_tag')} hint={t('ast.tag_hint', { tag: o.next_tag })}>
                <input className="input tabular" maxLength={40} value={tag} placeholder={o.next_tag} onChange={(e) => setTag(e.target.value)} />
              </Field>
            )}
            {n > 1 && !editing ? null : (
              <Field label={t('ast.serial')}>
                <input className="input" maxLength={80} value={serial} onChange={(e) => setSerial(e.target.value)} />
              </Field>
            )}
          </div>

          {editing ? null : (
            <div className="form-grid">
              <Field label={t('ast.source')}>
                <select className="input" value={source} onChange={(e) => setSource(e.target.value as Source)}>
                  {SOURCES.map((s) => (
                    <option key={s} value={s}>
                      {t(`ast.src_${s}` as MessageKey)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t('ast.acquired_on')} required>
                <input className="input" type="date" value={acquired} max={o.today} onChange={(e) => setAcquired(e.target.value)} />
              </Field>
              <Field label={n > 1 ? t('ast.cost_each') : t('ast.cost')} hint={t('ast.cost_hint')} error={tried ? errors.cost : null} required>
                <input className="input tabular" inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} />
              </Field>
            </div>
          )}
          {!editing && source === 'bought' && opts.bills.length ? (
            <Field label={t('ast.bill')} hint={pickedBill ? t('ast.bill_left', { amount: money(pickedBill.total - pickedBill.tax) }) : t('ast.bill_hint')}>
              <select className="input" value={bill} onChange={(e) => (setBill(e.target.value), e.target.value ? setSupplier('') : null)}>
                <option value="">{t('ast.no_bill')}</option>
                {opts.bills.map((b) => (
                  <option key={b.id} value={b.id}>
                    {t('ast.bill_x', { n: b.number, supplier: b.supplier, day: day(b.date), amount: money(b.total) })}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}

          <div className="form-grid">
            <Field label={t('ast.life_months')} hint={cat ? t('ast.life_hint', { life: life(cat.life_months) }) : undefined} error={tried ? errors.life : null}>
              <input className="input tabular" inputMode="numeric" value={lifeMonths} placeholder={cat ? String(cat.life_months) : ''} disabled={locked} onChange={(e) => setLifeMonths(e.target.value)} />
            </Field>
            <Field label={t('ast.residual')} hint={t('ast.residual_hint')} error={tried ? errors.residual : null}>
              <input className="input tabular" inputMode="decimal" value={residual} placeholder="0" disabled={locked} onChange={(e) => setResidual(e.target.value)} />
            </Field>
            {!bill ? (
              <Field label={t('ast.supplier')}>
                <input className="input" maxLength={120} value={supplier} onChange={(e) => setSupplier(e.target.value)} />
              </Field>
            ) : null}
          </div>
          {locked ? <p className="muted small">{t('ast.locked_note')}</p> : null}
          {!editing && existing ? (
            <div className="form-grid">
              <Field label={t('ast.before_amount')} hint={t('ast.before_hint')} error={tried ? errors.before : null}>
                <input className="input tabular" inputMode="decimal" value={before} placeholder="0" onChange={(e) => setBefore(e.target.value)} />
              </Field>
              <Field label={t('ast.before_months')}>
                <input className="input tabular" inputMode="numeric" value={monthsBefore} placeholder="0" onChange={(e) => setMonthsBefore(e.target.value)} />
              </Field>
            </div>
          ) : null}

          {editing ? null : (
            <div className="form-grid">
              <PlaceFields opts={opts} location={location} holder={holder} setLocation={setLocation} setHolder={setHolder} />
            </div>
          )}
          <Field label={t('ast.description')}>
            <textarea className="input" rows={2} maxLength={1000} value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>
        </div>
      )}
    </Dialog>
  );
}

function PlaceFields({ opts, location, holder, setLocation, setHolder }: { opts: Options | null; location: string; holder: string; setLocation: (v: string) => void; setHolder: (v: string) => void }) {
  return (
    <>
      <Field label={t('ast.where')} hint={t('ast.where_hint')}>
        <input className="input" maxLength={120} list="ast-places" value={location} onChange={(e) => setLocation(e.target.value)} />
        <datalist id="ast-places">
          {(opts?.locations ?? []).map((l) => (
            <option key={l} value={l} />
          ))}
        </datalist>
      </Field>
      <Field label={t('ast.holder')} hint={t('ast.holder_hint')}>
        <input className="input" maxLength={120} list="ast-holders" value={holder} onChange={(e) => setHolder(e.target.value)} />
        <datalist id="ast-holders">
          {[...new Set([...(opts?.holders ?? []), ...(opts?.people ?? []).map((p) => p.name)])].map((h) => (
            <option key={h} value={h} />
          ))}
        </datalist>
      </Field>
    </>
  );
}

/* ---------------------------------------------------------------- Moving and handing over */

function MoveDialog({ ids, rows, onClose, onDone }: { ids: string[]; rows: Asset[]; onClose: () => void; onDone: (handover: number) => Promise<void> }) {
  const { busy, run } = useSave();
  const { data: opts } = useAction<Options>('assets.options');
  const first = rows.find((r) => r.id === ids[0]);
  const [location, setLocation] = useState(ids.length === 1 ? first?.location ?? '' : '');
  const [holder, setHolder] = useState(ids.length === 1 ? first?.holder ?? '' : '');
  const [state, setState] = useState<State | ''>(ids.length === 1 ? first?.state ?? '' : '');
  const [date, setDate] = useState(todayIso());
  const [note, setNote] = useState('');
  const save = async () => {
    let n = 0;
    const ok = await run(async () => {
      const r = await action<{ moved: number; handover: number }>('assets.move', {
        ids,
        date,
        location: location.trim() || null,
        holder: holder.trim() || null,
        state: state || undefined,
        note: note.trim() || null,
      });
      n = r.handover;
    }, t('ast.moved_done'));
    if (ok) await onDone(n);
  };
  const names = rows.filter((r) => ids.includes(r.id));
  return (
    <Dialog
      title={ids.length === 1 && first ? t('ast.move_x', { tag: first.tag }) : tn('ast.move_n', ids.length)}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="transfer" onClick={save} busy={busy}>
            {t('ast.move_save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        {names.length > 1 ? <p className="muted">{names.map((r) => r.tag).join(', ')}</p> : null}
        <div className="form-grid">
          <PlaceFields opts={opts} location={location} holder={holder} setLocation={setLocation} setHolder={setHolder} />
          <Field label={t('ast.c_state')}>
            <select className="input" value={state} onChange={(e) => setState(e.target.value as State)}>
              {ids.length > 1 ? <option value="">{t('ast.keep_state')}</option> : null}
              {(['in_use', 'in_store', 'repair'] as State[]).map((s) => (
                <option key={s} value={s}>
                  {stateLabel(s)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('ast.date')}>
            <input className="input" type="date" value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)} />
          </Field>
        </div>
        <Field label={t('ast.note')} hint={t('ast.move_note_hint')}>
          <textarea className="input" rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <p className="muted small">{t('ast.handover_note')}</p>
      </div>
    </Dialog>
  );
}

interface Handover {
  number: number;
  date: string;
  note: string | null;
  by_name: string | null;
  from: { location: string | null; holder: string | null };
  to: { location: string | null; holder: string | null };
  items: { tag: string; name: string; serial: string | null; cost: number }[];
}

/** The printed hand-over record, with lines for the two signatures. */
function HandoverPrint({ number, onDone }: { number: number; onDone: () => void }) {
  const { tenant } = useSession();
  const { data } = useAction<Handover>('assets.handover', { number });
  const [printing, setPrinting] = useState(false);
  const print = () => {
    setPrinting(true);
    setTimeout(() => {
      window.print();
      setPrinting(false);
    }, 50);
  };
  const side = (s: Handover['from']) => [s.location, s.holder].filter(Boolean).join(' · ') || '—';
  return (
    <Dialog
      title={t('ast.handover_title', { n: number })}
      onClose={onDone}
      footer={
        <>
          <Button variant="ghost" onClick={onDone}>
            {t('ast.close')}
          </Button>
          <Button variant="primary" icon="printer" onClick={print} disabled={!data}>
            {t('ast.print')}
          </Button>
        </>
      }
    >
      {!data ? (
        <Skeleton h={160} />
      ) : (
        <div className="form-stack">
          <dl className="appr-facts">
            <dt>{t('ast.date')}</dt>
            <dd>{day(data.date)}</dd>
            <dt>{t('ast.from')}</dt>
            <dd>{side(data.from)}</dd>
            <dt>{t('ast.to')}</dt>
            <dd>{side(data.to)}</dd>
          </dl>
          <p className="muted">{data.items.map((i) => `${i.tag} ${i.name}`).join(', ')}</p>
        </div>
      )}
      {printing && data
        ? createPortal(
            <div className="print-sheet print-handover">
              <div className="ast-paper">
                <header>
                  <div>
                    <span className="ast-paper-org">{tenant?.name}</span>
                    <h1>{t('ast.handover_title', { n: data.number })}</h1>
                  </div>
                  <span>{day(data.date)}</span>
                </header>
                <dl>
                  <dt>{t('ast.from')}</dt>
                  <dd>{side(data.from)}</dd>
                  <dt>{t('ast.to')}</dt>
                  <dd>{side(data.to)}</dd>
                </dl>
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{t('ast.c_tag')}</th>
                      <th>{t('ast.name')}</th>
                      <th>{t('ast.serial')}</th>
                      <th className="num">{t('ast.cost')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((i, k) => (
                      <tr key={i.tag}>
                        <td>{k + 1}</td>
                        <td>{i.tag}</td>
                        <td>{i.name}</td>
                        <td>{i.serial ?? ''}</td>
                        <td className="num">{money(i.cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {data.note ? <p>{data.note}</p> : null}
                <div className="ast-sign">
                  <div>
                    <span>{t('ast.handed_by')}</span>
                    <span className="ast-sign-line" />
                  </div>
                  <div>
                    <span>{t('ast.received_by')}</span>
                    <span className="ast-sign-line" />
                  </div>
                </div>
                <footer>{t('ast.printed_by', { name: data.by_name ?? '' })}</footer>
              </div>
            </div>,
            document.body,
          )
        : null}
    </Dialog>
  );
}

/* ---------------------------------------------------------------- Disposal */

function DisposeDialog({ asset, onClose, onDone }: { asset: Asset; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [date, setDate] = useState(todayIso());
  const [kind, setKind] = useState<Disposal>('scrapped');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [reference, setReference] = useState('');
  const [tried, setTried] = useState(false);
  const minor = kind === 'sold' ? (amount.trim() ? parseMoney(amount) : 0) : 0;
  const errors = { reason: reason.trim().length >= 3 ? null : t('ast.reason_wrong'), amount: minor === null ? t('ast.cost_wrong') : null };
  const result = minor === null ? null : minor - asset.net;
  const save = async () => {
    setTried(true);
    if (errors.reason || errors.amount) return;
    if (await run(() => action('assets.dispose', { id: asset.id, date, kind, amount: minor, reason: reason.trim(), reference: reference.trim() || null }), t('ast.disposed_done', { tag: asset.tag })))
      await onDone();
  };
  return (
    <Dialog
      title={t('ast.dispose_x', { tag: asset.tag })}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" icon="archive" onClick={save} busy={busy}>
            {t('ast.dispose')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <p className="muted">{t('ast.dispose_body', { net: money(asset.net) })}</p>
        <div className="segmented" role="radiogroup" aria-label={t('ast.how')}>
          {DISPOSALS.map((d) => (
            <button key={d} role="radio" aria-checked={kind === d} className={kind === d ? 'active' : ''} onClick={() => setKind(d)}>
              {t(`ast.dk_${d}` as MessageKey)}
            </button>
          ))}
        </div>
        <div className="form-grid">
          <Field label={t('ast.date')}>
            <input className="input" type="date" value={date} min={asset.acquired_on} max={todayIso()} onChange={(e) => setDate(e.target.value)} />
          </Field>
          {kind === 'sold' ? (
            <Field label={t('ast.sold_for')} error={tried ? errors.amount : null}>
              <input className="input tabular" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </Field>
          ) : null}
          <Field label={t('ast.decision')} hint={t('ast.decision_hint')}>
            <input className="input" maxLength={80} value={reference} onChange={(e) => setReference(e.target.value)} />
          </Field>
        </div>
        <Field label={t('ast.reason')} error={tried ? errors.reason : null} required>
          <textarea className="input" rows={2} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} data-autofocus />
        </Field>
        {result !== null ? (
          <p className={`callout${result < 0 ? ' callout-warn' : ''}`}>
            <Icon name={result < 0 ? 'trendDown' : 'trendUp'} size={16} />
            <span>{result < 0 ? t('ast.loss_x', { amount: money(-result) }) : result > 0 ? t('ast.gain_x', { amount: money(result) }) : t('ast.no_gain')}</span>
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

/* ---------------------------------------------------------------- Depreciation */

interface Runs {
  runs: { id: string; number: number; until: string; from: string | null; assets: number; total: number; created_at: string; by_name: string | null }[];
  months: { period: string; amount: number; assets: number }[];
}

function DepreciationTab({ o, onRun }: { o: Overview; onRun: () => void }) {
  const { data } = useAction<Runs>('assets.runs');
  const d = o.depreciation;
  if (!data) return <Skeleton h={300} r="var(--radius-lg)" />;
  const months = [...data.months].reverse().slice(-24);
  return (
    <div className="form-stack">
      {d.behind ? (
        <p className="callout callout-warn">
          <Icon name="alert" size={16} />
          <span>{tn('ast.behind_body', d.behind, { month: monthName(d.due) })}</span>
          {o.can.manage ? (
            <Button size="sm" variant="primary" icon="trendDown" onClick={onRun}>
              {t('ast.depreciate')}
            </Button>
          ) : null}
        </p>
      ) : (
        <p className="callout">
          <Icon name="checkCircle" size={16} />
          <span>{d.last ? t('ast.up_to_date', { month: monthName(d.last.until) }) : t('ast.nothing_to_do')}</span>
        </p>
      )}
      {o.accounting ? <p className="muted small">{t('ast.posted_note')}</p> : null}
      {months.length ? (
        <section className="panel rep-wide">
          <div className="panel-head">
            <h2>{t('ast.by_month')}</h2>
          </div>
          <Bars items={months.map((m) => ({ key: m.period, label: monthName(m.period), value: m.amount, tip: `${monthName(m.period)}: ${money(m.amount)} · ${tn('ast.n_assets', m.assets)}` }))} />
        </section>
      ) : null}
      {data.runs.length ? (
        <Table
          head={[t('ast.c_run'), t('ast.c_months'), t('ast.c_assets'), t('ast.depreciation'), t('ast.c_by')]}
          phoneHide={[2, 4]}
          left={[0, 1, 4]}
          rows={data.runs.map((r) => ({
            key: r.id,
            cells: [
              <span className="tabular strong">{r.number}</span>,
              <span>{r.from && r.from !== r.until ? `${monthName(r.from)} – ${monthName(r.until)}` : monthName(r.until)}</span>,
              <span className="tabular">{r.assets}</span>,
              <span className="tabular">{money(r.total)}</span>,
              <span className="muted">{[r.by_name, dateTime(r.created_at)].filter(Boolean).join(' · ')}</span>,
            ],
          }))}
        />
      ) : (
        <EmptyState icon="trendDown" title={t('ast.no_runs')} body={t('ast.no_runs_body')} />
      )}
    </div>
  );
}

function RunDialog({ o, onClose, onDone }: { o: Overview; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const choices = Array.from({ length: 24 }, (_, i) => addMonths(o.depreciation.due, -i));
  const [until, setUntil] = useState(o.depreciation.due);
  const save = async () => {
    if (await run(() => action('assets.depreciate', { until }), t('ast.run_done', { month: monthName(until) }))) await onDone();
  };
  return (
    <Dialog
      title={t('ast.run_title')}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="trendDown" onClick={save} busy={busy}>
            {t('ast.depreciate')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <p className="muted">{t('ast.run_body')}</p>
        <Field label={t('ast.up_to')}>
          <select className="input" value={until} onChange={(e) => setUntil(e.target.value)} data-autofocus>
            {choices.map((m) => (
              <option key={m} value={m}>
                {monthName(m)}
              </option>
            ))}
          </select>
        </Field>
        {o.accounting ? <p className="muted small">{t('ast.posted_note')}</p> : null}
      </div>
    </Dialog>
  );
}

/* ---------------------------------------------------------------- Counting */

interface CountRow {
  id: string;
  number: number;
  name: string;
  date: string;
  location: string | null;
  state: 'open' | 'closed';
  closed_at: string | null;
  total: number;
  found: number;
  missing: number;
  todo: number;
}
interface CountLine {
  id: string;
  asset: string;
  tag: string;
  name: string;
  serial: string | null;
  register_location: string | null;
  holder: string | null;
  found: boolean | null;
  condition: 'good' | 'worn' | 'broken' | null;
  location: string | null;
  note: string | null;
}

function CountsTab({ o, onChange }: { o: Overview; onChange: () => Promise<void> }) {
  const { data, reload } = useAction<CountRow[]>('assets.counts');
  const [open, setOpen] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const changed = async () => {
    await Promise.all([reload(), onChange()]);
  };
  if (!data) return <Skeleton h={300} r="var(--radius-lg)" />;
  return (
    <div className="form-stack">
      <div className="ast-count-head">
        <p className="muted">{t('ast.counts_body')}</p>
        {o.can.count ? (
          <Button variant="primary" icon="list" onClick={() => setStarting(true)}>
            {t('ast.start_count')}
          </Button>
        ) : null}
      </div>
      {data.length ? (
        <Table
          head={[t('ast.c_count'), t('ast.date'), t('ast.where'), t('ast.c_found'), t('ast.c_missing'), t('ast.c_state')]}
          phoneHide={[1, 2, 4]}
          left={[0, 2]}
          rows={data.map((c) => ({
            key: c.id,
            onClick: () => setOpen(c.id),
            cells: [
              <span className="vat-party">
                <span className="strong">{c.name}</span>
                <span className="muted">{t('ast.count_no', { n: c.number })}</span>
              </span>,
              <span className="tabular">{day(c.date)}</span>,
              <span>{c.location ?? t('ast.everywhere')}</span>,
              <span className="tabular">
                {c.found}/{c.total}
              </span>,
              <span className={`tabular${c.missing ? ' text-danger strong' : ''}`}>{c.missing}</span>,
              c.state === 'open' ? <StatusBadge tone="info" icon="clock" label={tn('ast.to_check', c.todo)} /> : <StatusBadge tone="ok" icon="check" label={t('ast.count_closed')} />,
            ],
          }))}
        />
      ) : (
        <EmptyState icon="list" title={t('ast.no_counts')} body={t('ast.no_counts_body')} />
      )}
      {starting ? (
        <StartCount
          o={o}
          onClose={() => setStarting(false)}
          onDone={async (id) => {
            setStarting(false);
            await changed();
            setOpen(id);
          }}
        />
      ) : null}
      {open ? <CountDialog id={open} onClose={() => setOpen(null)} onChange={changed} /> : null}
    </div>
  );
}

function StartCount({ o, onClose, onDone }: { o: Overview; onClose: () => void; onDone: (id: string) => Promise<void> }) {
  const { busy, run } = useSave();
  const [name, setName] = useState(t('ast.count_default', { year: o.today.slice(0, 4) }));
  const [location, setLocation] = useState('');
  const save = async () => {
    let id = '';
    if (await run(async () => void (id = (await action<{ id: string }>('assets.start_count', { name: name.trim(), location: location || null })).id))) await onDone(id);
  };
  return (
    <Dialog
      title={t('ast.start_count')}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="list" onClick={save} busy={busy} disabled={!name.trim()}>
            {t('ast.start')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <Field label={t('ast.count_name')}>
          <input className="input" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} data-autofocus />
        </Field>
        <Field label={t('ast.where')} hint={t('ast.count_where_hint')}>
          <select className="input" value={location} onChange={(e) => setLocation(e.target.value)}>
            <option value="">{t('ast.everywhere')}</option>
            {o.locations.map((l) => (
              <option key={l.location} value={l.location}>
                {l.location} ({l.n})
              </option>
            ))}
          </select>
        </Field>
      </div>
    </Dialog>
  );
}

function CountDialog({ id, onClose, onChange }: { id: string; onClose: () => void; onChange: () => Promise<void> }) {
  const { data, reload } = useAction<{ count: CountRow; lines: CountLine[]; can: { count: boolean } }>('assets.count', { id });
  const { busy, run } = useSave();
  const [closing, setClosing] = useState(false);
  const mark = (l: CountLine, change: Partial<CountLine>) =>
    void run(async () => {
      const next = { ...l, ...change };
      await action('assets.mark', { count: id, lines: [{ id: l.id, found: next.found, condition: next.found ? next.condition : null, location: next.found ? next.location : null, note: next.note }] });
      await reload();
    });
  const close = async () => {
    if (await run(() => action('assets.close_count', { id }), t('ast.count_closed_done'))) {
      setClosing(false);
      await Promise.all([reload(), onChange()]);
    }
  };
  if (!data) {
    return (
      <Dialog title={t('ast.count')} onClose={onClose} size="lg">
        <Skeleton h={260} />
      </Dialog>
    );
  }
  const c = data.count;
  const todo = data.lines.filter((l) => l.found === null).length;
  const editable = data.can.count;
  return (
    <Dialog
      title={c.name}
      onClose={onClose}
      size="lg"
      footer={
        editable ? (
          <>
            <span className="muted">{todo ? tn('ast.to_check', todo) : t('ast.all_checked')}</span>
            <Button variant="primary" icon="check" onClick={() => setClosing(true)} disabled={todo > 0}>
              {t('ast.close_count')}
            </Button>
          </>
        ) : undefined
      }
    >
      <div className="form-stack">
        <p className="muted">
          {[t('ast.count_no', { n: c.number }), day(c.date), c.location ?? t('ast.everywhere'), c.state === 'closed' ? t('ast.count_closed') : null].filter(Boolean).join(' · ')}
        </p>
        <div className="list-card ast-count-list">
          {data.lines.map((l) => (
            <div key={l.id} className={`ast-count-row${l.found === false ? ' missing' : ''}`}>
              <span className="list-main">
                <span className="list-title">
                  <span className="tabular">{l.tag}</span> · {l.name}
                </span>
                <span className="list-sub">{[l.register_location, l.holder, l.serial].filter(Boolean).join(' · ') || ' '}</span>
              </span>
              {editable ? (
                <span className="ast-count-actions">
                  <span className="segmented" role="radiogroup" aria-label={t('ast.found_q', { tag: l.tag })}>
                    <button role="radio" aria-checked={l.found === true} className={l.found === true ? 'active' : ''} disabled={busy} onClick={() => mark(l, { found: true, condition: l.condition ?? 'good' })}>
                      <Icon name="check" size={14} /> {t('ast.found')}
                    </button>
                    <button role="radio" aria-checked={l.found === false} className={l.found === false ? 'active' : ''} disabled={busy} onClick={() => mark(l, { found: false })}>
                      <Icon name="x" size={14} /> {t('ast.missing')}
                    </button>
                  </span>
                  {l.found ? (
                    <>
                      <select className="input" value={l.condition ?? 'good'} disabled={busy} onChange={(e) => mark(l, { condition: e.target.value as CountLine['condition'] })} aria-label={t('ast.condition')}>
                        {(['good', 'worn', 'broken'] as const).map((k) => (
                          <option key={k} value={k}>
                            {t(`ast.cond_${k}` as MessageKey)}
                          </option>
                        ))}
                      </select>
                      <input
                        className="input"
                        maxLength={120}
                        defaultValue={l.location ?? ''}
                        placeholder={t('ast.found_where')}
                        aria-label={t('ast.found_where')}
                        onBlur={(e) => (e.target.value.trim() || null) !== l.location && mark(l, { location: e.target.value.trim() || null })}
                      />
                    </>
                  ) : null}
                </span>
              ) : (
                <span className="ast-count-result">
                  {l.found ? <StatusBadge tone="ok" icon="check" label={[t('ast.found'), l.condition && l.condition !== 'good' ? t(`ast.cond_${l.condition}` as MessageKey) : null, l.location].filter(Boolean).join(' · ')} /> : <StatusBadge tone="danger" icon="x" label={t('ast.missing')} />}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
      {closing ? (
        <Dialog
          title={t('ast.close_count')}
          onClose={() => setClosing(false)}
          size="sm"
          footer={
            <>
              <Button variant="ghost" onClick={() => setClosing(false)}>
                {t('common.cancel')}
              </Button>
              <Button variant="primary" icon="check" onClick={close} busy={busy}>
                {t('ast.close_count')}
              </Button>
            </>
          }
        >
          <p>{t('ast.close_count_body')}</p>
        </Dialog>
      ) : null}
    </Dialog>
  );
}

/* ---------------------------------------------------------------- Categories */

function CategoriesTab({ o, onChange }: { o: Overview; onChange: () => Promise<void> }) {
  const [editing, setEditing] = useState<Category | 'new' | null>(null);
  return (
    <div className="form-stack">
      <div className="ast-count-head">
        <p className="muted">{t('ast.categories_body')}</p>
        {o.can.manage ? (
          <Button icon="plus" onClick={() => setEditing('new')}>
            {t('ast.new_category')}
          </Button>
        ) : null}
      </div>
      {o.categories.length ? (
        <Table
          head={[t('ast.code'), t('ast.name'), t('ast.life'), t('ast.c_assets'), t('ast.cost')]}
          phoneHide={[0, 4]}
          left={[0, 1, 2]}
          rows={o.categories.map((c) => ({
            key: c.id,
            onClick: o.can.manage ? () => setEditing(c) : undefined,
            cells: [
              <span className="tabular">{c.code ?? '—'}</span>,
              <span className={c.active ? '' : 'muted'}>
                {c.name}
                {c.active ? '' : ` · ${t('ast.not_in_use')}`}
              </span>,
              <span>{c.method === 'none' ? t('ast.no_depreciation') : life(c.life_months)}</span>,
              <span className="tabular">{c.n}</span>,
              <span className="tabular">{money(c.cost)}</span>,
            ],
          }))}
        />
      ) : (
        <EmptyState icon="layers" title={t('ast.no_categories')} body={t('ast.no_categories_body')} />
      )}
      {editing ? (
        <CategoryForm
          category={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onDone={async () => {
            setEditing(null);
            await onChange();
          }}
        />
      ) : null}
    </div>
  );
}

function CategoryForm({ category, onClose, onDone }: { category: Category | null; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [code, setCode] = useState(category?.code ?? '');
  const [name, setName] = useState(category?.name ?? '');
  const [years, setYears] = useState(category ? String(category.life_months / 12) : '5');
  const [method, setMethod] = useState<Method>(category?.method ?? 'straight_line');
  const [active, setActive] = useState(category?.active ?? true);
  const [tried, setTried] = useState(false);
  const months = Math.round(Number(years.replace(',', '.')) * 12);
  const errors = { name: name.trim() ? null : t('form.required'), life: method === 'none' || (months >= 1 && months <= 1200) ? null : t('ast.life_wrong') };
  const save = async () => {
    setTried(true);
    if (errors.name || errors.life) return;
    if (
      await run(
        () => action('assets.save_category', { id: category?.id, code: code.trim() || null, name: name.trim(), life_months: method === 'none' ? 1 : months, method, active }),
        t('ast.category_saved'),
      )
    )
      await onDone();
  };
  return (
    <Dialog
      title={category ? category.name : t('ast.new_category')}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" onClick={save} busy={busy}>
            {t('ast.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <div className="form-grid">
          <Field label={t('ast.code')} hint={t('ast.code_hint')}>
            <input className="input tabular" maxLength={20} value={code} onChange={(e) => setCode(e.target.value)} />
          </Field>
          <Field label={t('ast.name')} error={tried ? errors.name : null} required>
            <input className="input" maxLength={80} value={name} onChange={(e) => setName(e.target.value)} data-autofocus />
          </Field>
        </div>
        <Field label={t('ast.method')}>
          <select className="input" value={method} onChange={(e) => setMethod(e.target.value as Method)}>
            <option value="straight_line">{t('ast.m_straight_line')}</option>
            <option value="none">{t('ast.m_none')}</option>
          </select>
        </Field>
        {method === 'straight_line' ? (
          <Field label={t('ast.life_years')} hint={t('ast.life_years_hint')} error={tried ? errors.life : null}>
            <input className="input tabular" inputMode="decimal" value={years} onChange={(e) => setYears(e.target.value)} />
          </Field>
        ) : null}
        {category ? (
          <label className="look-toggle">
            <span>
              <span className="strong">{t('ast.in_use_toggle')}</span>
              <span className="muted small">{t('ast.in_use_toggle_hint')}</span>
            </span>
            <Switch checked={active} onChange={setActive} label={t('ast.in_use_toggle')} />
          </label>
        ) : null}
      </div>
    </Dialog>
  );
}
